import React, { useState, useEffect } from 'react';
import { Save, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { User } from '../types';
import MailAccounts from './MailAccounts';

interface Props {
  currentUser: User;
}

type Form = Record<string, string>;

const DEFAULTS: Form = {
  MAIL_POLL_MINUTES: '30',
  SCREEN_MIN_EDU: '大专',
  SCREEN_MAX_AGE: '40',
  SCREEN_JOBHOP_YEARS: '5',
  SCREEN_JOBHOP_COUNT: '3',
  SCREEN_JOBHOP_AVG: '1',
  SCREEN_FRESH_EXEMPT_YEARS: '2',
  SCREEN_RECOMMEND_SCORE: '8',
  SCREEN_REVIEW_SCORE: '6',
  DIGEST_ENABLED: '1',
  DIGEST_TIME: '08:00',
  DIGEST_TOP_N: '10',
};

const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';

export default function MailIntakeSettings({ currentUser }: Props) {
  const [form, setForm] = useState<Form>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await api.get('/admin/settings').then(r => r.data);
      const next: Form = { ...DEFAULTS };
      Object.keys(DEFAULTS).forEach(k => {
        if (data[k] !== undefined && data[k] !== '') next[k] = String(data[k]);
      });
      setForm(next);
    } catch (e: any) {
      toast.error('加载失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  const set = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }));

  async function handleSave() {
    setSaving(true);
    try {
      await api.post('/admin/settings', { ...form });
      toast.success('设置已保存，立即生效');
      await load();
    } catch (e: any) {
      toast.error('保存失败: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  }

  const toggle = (k: string, label: string) => (
    <button
      type="button"
      onClick={() => set(k, form[k] === '1' ? '0' : '1')}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form[k] === '1' ? 'bg-primary-600' : 'bg-gray-300'}`}
      title={label}
    >
      <span className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform" style={{ transform: form[k] === '1' ? 'translateX(18px)' : 'translateX(3px)' }} />
    </button>
  );

  const numField = (k: string, label: string, hint?: string) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input type="number" value={form[k]} onChange={e => set(k, e.target.value)} className={inputCls} />
      {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Mailbox accounts (admin sees all) */}
      <MailAccounts currentUser={currentUser} />

      {/* Global screening rules & digest */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="font-semibold text-gray-800 text-sm">📬 邮箱初筛 · 全局规则</h2>
          <p className="text-xs text-gray-500 mt-0.5">AI 按岗位 JD 初筛的统一标准与飞书晨报设置（对所有邮箱账户生效）</p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-400 py-8 justify-center">
            <RefreshCw size={16} className="animate-spin" /><span>加载中...</span>
          </div>
        ) : (
        <div className="p-5 space-y-6">
        {/* Screening thresholds */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">初筛规则（可随时调整）</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">学历下限</label>
              <select value={form.SCREEN_MIN_EDU} onChange={e => set('SCREEN_MIN_EDU', e.target.value)} className={inputCls}>
                {['博士', '硕士', '本科', '大专', '中专', '高中'].map(o => <option key={o} value={o}>{o}及以上</option>)}
              </select>
              <p className="text-[11px] text-gray-400 mt-0.5">低于此学历直接淘汰</p>
            </div>
            {numField('SCREEN_MAX_AGE', '年龄上限', '超过则淘汰（0=不限）')}
            {numField('SCREEN_JOBHOP_YEARS', '跳槽统计窗口(年)', '统计近 N 年任职')}
            {numField('SCREEN_JOBHOP_COUNT', '跳槽公司数阈值', '近 N 年≥此数')}
            {numField('SCREEN_JOBHOP_AVG', '平均任职<年', '且平均任职低于此年数→淘汰')}
            {numField('SCREEN_FRESH_EXEMPT_YEARS', '应届豁免(工作年限<)', '低于此年限免年龄/跳槽规则')}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            {numField('SCREEN_RECOMMEND_SCORE', '推荐分数线', 'AI≥此分→推荐(重点关注)')}
            {numField('SCREEN_REVIEW_SCORE', '二筛分数线', '此分~推荐线之间→待二筛；更低→淘汰')}
          </div>
        </div>

        {/* Digest */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">飞书晨报</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2">
              <span className="text-xs font-medium text-gray-600">每日推送晨报</span>
              {toggle('DIGEST_ENABLED', '晨报')}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">推送时间</label>
              <input type="time" value={form.DIGEST_TIME} onChange={e => set('DIGEST_TIME', e.target.value)} className={inputCls} />
            </div>
            {numField('DIGEST_TOP_N', '详列推荐人数上限', '卡片中最多列出的推荐数')}
          </div>
          <p className="text-[11px] text-gray-400 mt-2">晨报默认推送给所有绑定飞书的管理员/HR。≥推荐线全部列出，6-7.9分计入待二筛，淘汰仅计数。</p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end pt-2 border-t border-gray-100">
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-primary-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} {saving ? '保存中...' : '保存全局规则'}
          </button>
        </div>
        </div>
        )}
      </div>
    </div>
  );
}
